import { useContext, useCallback, useEffect, useMemo } from "react";
import { ConfiguratorContext } from "../context";
import useConfiguratorData from "./useConfiguratorData";
import useMAFSimulationData from "./useMAFSimulationData";
import useKintoSimulationData from "./useKintoSimulationData";
import useAccessoriesData from "./useAccessoriesData";
import usePersonalData from "./usePersonalData";

/**
 * Hook para manejo de opciones de pago en v2
 * Gestiona selección entre contado, financiamiento MAF y Kinto
 */

// Configuración de opciones de pago disponibles
export const PAYMENT_OPTIONS = [
    {
        id: 1,
        name: "Contado",
        description: "Pago único del valor total del vehículo",
        hasSimulation: false,
    },
    {
        id: 2,
        name: "Financiamiento MAF",
        nameWithResult: "Financiamiento MAF",
        description: "Crédito automotriz con cuotas fijas",
        hasSimulation: true,
    },
    {
        id: 3,
        name: "Alquiler por cuotas mensuales | ",
        nameWithResult: "Cuotas mensuales | ",
        description: "Suscripción mensual con Kinto",
        hasSimulation: true,
        hasLogo: true,
    },
];

const usePaymentOptions = () => {
    const [state, setState] = useContext(ConfiguratorContext);
    const { getSelectedVersion, setSelectedPrepaidMaintenancePlan } = useConfiguratorData();
    const { resetForEdit: resetMAFForEdit } = useMAFSimulationData();
    const { resetForEdit: resetKintoForEdit } = useKintoSimulationData();
    const { getAddedAccessories, checkAddedAccessory } = useAccessoriesData();
    const { isCompany } = usePersonalData();

    const selectedPaymentOption = state.selectedPaymentOption;
    const simulationResults = state.simulationResults;
    const simulationKeys = state.simulationKeys;
    const selectedVersion = getSelectedVersion();
    // Empresa cotiza KINTO One Business; persona natural, One Personal
    const isCompanyClient = isCompany();

    // El vehículo debe estar habilitado para el producto Kinto que corresponde
    // al tipo de cliente. El backend valida lo mismo en la simulación
    // (product 1 -> visible_on_one_personal, product 2 -> visible_on_one_business).
    const kintoAvailable = useMemo(() => {
        const kintoCar = selectedVersion?.kinto_car;

        if (!kintoCar) return false;

        return Boolean(
            isCompanyClient
                ? kintoCar.visible_on_one_business
                : kintoCar.visible_on_one_personal
        );
    }, [selectedVersion, isCompanyClient]);

    // Filtra opciones disponibles según si el vehículo soporta Kinto
    const paymentOptions = useMemo(() => {
        if (kintoAvailable) {
            // Si tiene configuración Kinto, mostrar todas las opciones
            return PAYMENT_OPTIONS;
        } else {
            // Si no tiene Kinto, excluir la opción de alquiler mensual
            return PAYMENT_OPTIONS.filter((option) => option.id !== 3);
        }
    }, [kintoAvailable]);

    // Efecto para resetear la opción de pago si se selecciona Kinto pero la versión no lo soporta
    useEffect(() => {
        if (selectedPaymentOption === 3 && !kintoAvailable) {
            // Si está seleccionado Kinto pero no aplica al producto/versión, cambiar a contado
            setState((prevState) => ({
                ...prevState,
                selectedPaymentOption: 1, // Contado
            }));
        }
    }, [kintoAvailable, selectedPaymentOption, setState]);

    // Console log para debugging de la opción seleccionada
    useEffect(() => {
        const selectedOption = PAYMENT_OPTIONS.find(
            (option) => option.id === selectedPaymentOption
        );
    }, [selectedPaymentOption]);

    // Cambiar opción de pago seleccionada
    const handleOptionChange = useCallback(
        (optionId, optionName) => {
            setState((prevState) => ({
                ...prevState,
                selectedPaymentOption: optionId,
            }));

            // Si se cambia a Kinto (id=3), deseleccionar mantenciones prepagadas y limpiar accesorios
            if (optionId === 3) {
                setSelectedPrepaidMaintenancePlan(null);
                
                // Limpiar todos los accesorios agregados
                const addedAccessories = getAddedAccessories();
                addedAccessories.forEach(accessory => {
                    checkAddedAccessory(accessory);
                });
            }
            
            // Si se cambia a Contado (id=1), solo deseleccionar mantenciones prepagadas
            if (optionId === 1) {
                setSelectedPrepaidMaintenancePlan(null);
                if(dataLayer) {
                    dataLayer.push({
                        'event': 'cotizador',
                        'eventCategory': 'opciones_de_pago',
                        'eventAction': 'cta',
                        'eventLabel': optionName.replace(/\s+/g, '_').toLowerCase()
                    });
                    
                }
            }
        },
        [setState, setSelectedPrepaidMaintenancePlan, getAddedAccessories, checkAddedAccessory]
    );

    // Manejar cambio en resultados de simulación
    const handleResultChange = useCallback(
        (optionId, showingResult) => {
            setState((prevState) => ({
                ...prevState,
                simulationResults: {
                    ...prevState.simulationResults,
                    [optionId]: showingResult,
                },
            }));
        },
        [setState]
    );

    // Editar simulación (resetear resultado pero mantener valores)
    const handleEditSimulation = useCallback(
        (optionId) => {
            if (optionId === 2) {
                // MAF
                // Para MAF, solo ocultar resultado pero mantener valores
                resetMAFForEdit();
            } else if (optionId === 3) {
                // Kinto
                // Para Kinto, solo ocultar resultado pero mantener valores
                resetKintoForEdit();
            } else {
                // Para otros simuladores, mantener comportamiento original
                setState((prevState) => ({
                    ...prevState,
                    simulationKeys: {
                        ...prevState.simulationKeys,
                        [optionId]: prevState.simulationKeys[optionId] + 1,
                    },
                }));
            }

            // Siempre ocultar el resultado
            setState((prevState) => ({
                ...prevState,
                simulationResults: {
                    ...prevState.simulationResults,
                    [optionId]: false,
                },
            }));
        },
        [setState, resetMAFForEdit, resetKintoForEdit]
    );

    // Obtener información de la opción seleccionada
    const getSelectedOption = useCallback(() => {
        return PAYMENT_OPTIONS.find((option) => option.id === selectedPaymentOption);
    }, [selectedPaymentOption]);

    // Verificar si es pago al contado
    const isCash = selectedPaymentOption === 1;

    // Verificar si es financiamiento MAF
    const isMAF = selectedPaymentOption === 2;

    // Verificar si es Kinto
    const isKinto = selectedPaymentOption === 3;

    return {
        isCompany: isCompanyClient,
        // Estado
        selectedPaymentOption,
        simulationResults,
        simulationKeys,
        paymentOptions, // Ya no es la constante PAYMENT_OPTIONS, sino la versión filtrada

        // Handlers
        handleOptionChange,
        handleResultChange,
        handleEditSimulation,

        // Utilidades
        getSelectedOption,
        isCash,
        isMAF,
        isKinto,
    };
};

export default usePaymentOptions;
